/**
 * Indicateurs tangibles des services publics (PRD §14.3, ADR 0005, ADR 0016). Entièrement décrits par `params`, aucun
 * identifiant d'indicateur ni de mission dans le code. Deux mécaniques :
 *
 * 1. **Identité de capacité** (ADR 0016) quand `svcind_<clé>_capacity_base` existe : l'indicateur est un ratio
 *    demande / capacité (détenus / places, élèves / classes, stock d'affaires / sorties). La demande est tenue à son niveau
 *    de départ (convention explicite, ADR 0016 §3) ; la capacité (places, enseignants, ETPT de juridiction) bouge :
 *    - avec les crédits de la mission quand `capacity_from_credits` est vrai (personnel : une variation relative des crédits
 *      de la mission, rabot ou économie ciblée, se transmet à l'identique à la capacité — coût moyen constant, allocation au
 *      prorata) ; jamais pour une capacité physique (places de prison : seuls les programmes de construction en ajoutent) ;
 *    - avec les quantités physiques des mesures (`fx.capacity.<clé>`, unités de la capacité, issues de leur décomposition
 *      sourcée), au profil de la mesure (montée en charge, ou profil daté pour une livraison après délai).
 *    Valeur = base × capacité_départ / capacité. Hausse et baisse passent par la même identité (seule non-linéarité : le ratio).
 * 2. **Élasticité** (ADR 0005, convention v0.2) sinon : valeur = base × (1 − élasticité × (indice − 1)), plancher 0.
 */
import { param } from "./params.js";
import type { ParamSet } from "./types.js";

export interface ServiceIndicatorDef {
  key: string;
  mission: string;
  base: number;
  label: string;
  unit: string;
  /** Mécanique 2 (convention) : élasticité à l'indice de service. Absente si identité. */
  elasticity?: number;
  /** Mécanique 1 (identité) : capacité de départ et transmission des crédits de la mission. */
  capacity?: { base: number; from_credits: boolean };
}

/** Entrées de l'identité de capacité pour une année. */
export interface CapacityInputs {
  /** Variation relative des crédits de chaque mission par rapport au tendanciel (−0,1 = −10 %), hors mesures. */
  credit_change: Record<string, number>;
  /** Unités de capacité ajoutées (ou retirées) par les mesures, par indicateur. */
  added: Record<string, number>;
}

const RE = /^svcind_([a-z0-9_]+)_base$/;

/** Définitions lues dans `params` (ordre des paramètres). */
export function serviceIndicatorDefs(params: ParamSet): ServiceIndicatorDef[] {
  const out: ServiceIndicatorDef[] = [];
  for (const [id, p] of Object.entries(params)) {
    const m = RE.exec(id);
    if (!m || !m[1] || m[1].endsWith("_capacity")) continue;
    if (!p.mission) throw new Error(`params : « ${id} » doit préciser sa mission (champ mission)`);
    const key = m[1];
    const capId = `svcind_${key}_capacity_base`;
    const cap = params[capId];
    const d: ServiceIndicatorDef = { key, mission: p.mission, base: param(params, id), label: p.label, unit: p.unit };
    if (cap) d.capacity = { base: param(params, capId), from_credits: cap.capacity_from_credits === true };
    else d.elasticity = param(params, `svcind_${key}_elasticity`);
    out.push(d);
  }
  return out;
}

/**
 * Valeurs des indicateurs. `serviceIndex` sert la mécanique 2 ; `capacity` la mécanique 1 (absent = aucune variation).
 * Mission absente de la baseline, ou capacité ≤ 0, → erreur de contenu.
 */
export function serviceIndicators(defs: ServiceIndicatorDef[], serviceIndex: Record<string, number>, capacity?: CapacityInputs): Record<string, number> {
  const out: Record<string, number> = {};
  for (const d of defs) {
    const s = serviceIndex[d.mission];
    if (s === undefined) throw new Error(`indicateur « ${d.key} » : mission inconnue « ${d.mission} »`);
    if (d.capacity) {
      const c0 = d.capacity.base;
      const rel = d.capacity.from_credits ? (capacity?.credit_change[d.mission] ?? 0) : 0;
      const c = c0 * (1 + rel) + (capacity?.added[d.key] ?? 0);
      if (!(c > 0)) throw new Error(`indicateur « ${d.key} » : capacité nulle ou négative (${c})`);
      out[d.key] = (d.base * c0) / c;
    } else {
      out[d.key] = Math.max(0, d.base * (1 - (d.elasticity ?? 0) * (s - 1)));
    }
  }
  return out;
}
